// Propose-form validation and wire-body construction, driven entirely by `service/actionCatalogue`.
//
// Client-side validation only catches what the backend would reject as `VALIDATION_FAILED` — it
// deliberately does NOT re-implement guardrails (delta thresholds, "already in that status",
// "schedule_update needs start_time, end_time, or both"): those are the backend's judgement and come
// back as `GUARDRAIL_VIOLATION` with reasons to display.
//
// Errors are i18n KEY SUFFIXES, never copy: components render `platformActions.validation.<key>`.
import type { PlatformActionPlatform, ProposeActionInput } from '../models/platformAction.types'
import type { ProposeBodyDto } from '../models/platformAction.wire'

import { ACTION_CATALOGUE, type ActionDescriptor, type PayloadField } from './actionCatalogue'

export const BUDGET_UPDATE = 'campaign.budget_update'
export const STATUS_UPDATE = 'campaign.status_update'
export const CAMPAIGN_CREATE = 'campaign.create'

export const PLATFORM_OPTIONS = ['google', 'meta', 'tiktok'] as const

/** Which id the backend anchors an action on (handoff cr-14 + § Target level). */
export type ProposeAnchor = 'campaign' | 'adAccount'

export type ProposeFieldError = 'required' | 'positive' | 'invalidJson'

export interface ProposeErrors {
  campaignId?: ProposeFieldError
  targetId?: ProposeFieldError
  /** Keyed by the descriptor's WIRE key, so the form can look an error up by field. */
  payload: Record<string, ProposeFieldError>
}

export interface ProposeValidation {
  valid: boolean
  errors: ProposeErrors
  /** The wire body — `null` whenever `valid` is false. */
  body: ProposeBodyDto | null
}

export function descriptorFor(actionType: string): ActionDescriptor | null {
  return ACTION_CATALOGUE[actionType] ?? null
}

/** Every action the chosen platform actually implements — the rest answer `422 UNKNOWN_ACTION`. */
export function actionsForPlatform(platform: string): string[] {
  return Object.keys(ACTION_CATALOGUE).filter((name) =>
    ACTION_CATALOGUE[name]?.platforms.includes(platform as PlatformActionPlatform),
  )
}

/**
 * A create has no entity to point at yet. `campaign.create` therefore anchors on the ad account,
 * while `ad_set.create` / `ad.create` keep the ordinary PARENT anchor — they are created inside a
 * campaign that already exists.
 */
export function anchorFor(actionType: string): ProposeAnchor {
  const descriptor = descriptorFor(actionType)
  return descriptor?.creates && descriptor.level === 'campaign' ? 'adAccount' : 'campaign'
}

/**
 * Sub-level actions name the child they touch. A create never does: `target_id` is an OUTPUT there,
 * refused on the way in and stamped only once the request succeeds.
 */
export function needsTargetId(actionType: string): boolean {
  const descriptor = descriptorFor(actionType)
  return descriptor !== null && !descriptor.creates && descriptor.level !== 'campaign'
}

export function isFieldRequired(field: PayloadField, platform: string): boolean {
  if (field.required === true) return true
  return (
    Array.isArray(field.required) && field.required.includes(platform as PlatformActionPlatform)
  )
}

/** The fields this platform can fill in — another platform's mandatory key is noise here. */
export function fieldsFor(actionType: string, platform: string): PayloadField[] {
  return (descriptorFor(actionType)?.fields ?? []).filter(
    (field) =>
      !Array.isArray(field.required) || field.required.includes(platform as PlatformActionPlatform),
  )
}

export function emptyProposeInput(): ProposeActionInput {
  return {
    platform: 'google',
    actionType: BUDGET_UPDATE,
    campaignId: '',
    adAccountId: '',
    targetId: '',
    marketingChannel: '',
    payload: {},
  }
}

function coerce(field: PayloadField, raw: string): unknown {
  const value = raw.trim()
  switch (field.type) {
    case 'number':
      return Number(value)
    // Blank is the ANSWER here, not a gap: `spend_cap: null` removes the ceiling.
    case 'nullableNumber':
      return value === '' ? null : Number(value)
    case 'list':
      return value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
    case 'json':
      return JSON.parse(value)
    default:
      return value
  }
}

function fieldError(field: PayloadField, raw: string, required: boolean): ProposeFieldError | null {
  const value = raw.trim()

  // An empty list IS an answer ("no regulated category"), and an empty nullable number IS null.
  if (value === '' && (field.type === 'list' || field.type === 'nullableNumber')) return null
  if (value === '') return required ? 'required' : null

  if (field.type === 'number' || field.type === 'nullableNumber') {
    const amount = Number(value)
    if (!Number.isFinite(amount)) return 'required'
    // Zero or negative is a guardrail violation server-side; catching it here saves a round trip on
    // the one mistake the form can be certain about.
    if (amount <= 0) return 'positive'
  }

  if (field.type === 'json') {
    try {
      JSON.parse(value)
    } catch {
      return 'invalidJson'
    }
  }

  return null
}

function buildPayload(input: ProposeActionInput, fields: PayloadField[]): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  for (const field of fields) {
    const raw = input.payload[field.key] ?? ''
    // An optional field the user left blank is absent, not empty — the backend reads `""` and
    // `null` as distinct values, never as "not supplied".
    if (!isFieldRequired(field, input.platform) && raw.trim() === '') continue
    payload[field.key] = coerce(field, raw)
  }
  return payload
}

export function validateProposeInput(input: ProposeActionInput): ProposeValidation {
  const errors: ProposeErrors = { payload: {} }
  const fields = fieldsFor(input.actionType, input.platform)
  const anchor = anchorFor(input.actionType)

  if (anchor === 'campaign' && input.campaignId.trim().length === 0) errors.campaignId = 'required'
  if (needsTargetId(input.actionType) && input.targetId.trim().length === 0) {
    errors.targetId = 'required'
  }

  for (const field of fields) {
    const problem = fieldError(
      field,
      input.payload[field.key] ?? '',
      isFieldRequired(field, input.platform),
    )
    if (problem) errors.payload[field.key] = problem
  }

  const valid =
    errors.campaignId === undefined &&
    errors.targetId === undefined &&
    Object.keys(errors.payload).length === 0
  if (!valid) return { valid, errors, body: null }

  const body: ProposeBodyDto = {
    platform: input.platform,
    action_type: input.actionType,
    payload: buildPayload(input, fields),
  }

  if (anchor === 'campaign') {
    body.campaign_id = input.campaignId.trim()
    // Only sent when the user disambiguates a campaign id that exists on several channels; an empty
    // string would be a distinct (invalid) value to the backend, not "absent".
    const channel = input.marketingChannel.trim()
    if (channel) body.marketing_channel = channel
    if (needsTargetId(input.actionType)) body.target_id = input.targetId.trim()
  } else {
    // Optional by design: with exactly one active account on the platform the backend picks it. With
    // several it answers `422` + `details.candidates`, which the modal turns into a chooser.
    const account = input.adAccountId.trim()
    if (account) body.ad_account_id = account
  }

  return { valid, errors, body }
}
